/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { handleError } from '@/utils/handelErrorMessage';
import { toast } from 'sonner';
import { Component } from 'types/api-data';
import { IDependencies } from 'types/services';

import { useComponents } from '@/hooks/useComponents';
import { ConfirmationDialog } from '@/components/confirmation-alert-dialog';

interface ArchiveComponentProps {
  isOpen: boolean;
  onClose: () => void;
  component: Component;
  onSuccess?: () => void;
  onDisassociatingApps?: (isLoading: boolean) => void;
}

export const ArchiveComponent: React.FC<ArchiveComponentProps> = ({
  isOpen,
  onClose,
  component,
  onSuccess,
  onDisassociatingApps,
}) => {
  const { messages } = useLanguage();
  const dialogStrings = messages?.ComponentsPage;
  const {
    state: { archivingComponent },
    componentsActions,
  } = useComponents();

  const [isAppDisassociated, setIsAppDisassociated] = useState(false);
  const hasAssociatedApps = (component?.apps?.length ?? 0) > 0;
  const totalAssociatedApps = component?.apps?.length ?? 0;

  // Reset checkbox and disassociation when modal is open
  useEffect(() => {
    if (isOpen) {
      setIsAppDisassociated(false);
      onDisassociatingApps?.(false);
    }
  }, [isOpen]);

  const handleArchive = async () => {
    try {
      if (hasAssociatedApps) {
        // disassociation starts
        onDisassociatingApps?.(true);
        // Keep only 'served_by' dependency if it exists
        const dependenciesToKeep: IDependencies[] = component.served_by
          ? [
              {
                target_name: component.served_by.component_name,
                dependency_type: 'served_by',
              },
            ]
          : [];

        // Update component's dependencies by disassociating all apps
        await componentsActions.updateComponentDependencies(
          component.component_name,
          dependenciesToKeep
        );
        onDisassociatingApps?.(false);
      }

      const archivePayload = {
        ...component,
        status: 'archived',
      };
      const archiveResponse = await componentsActions.archiveComponent(
        component.component_name,
        archivePayload
      );

      if (archiveResponse) {
        toast.success(dialogStrings?.component_archived_successfully_msg);
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Error archiving component:', error);
      onDisassociatingApps?.(false);
      handleError(error, dialogStrings?.error_component_archived_msg);
    }
  };

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onClose={onClose}
      title={dialogStrings?.archive_component_title}
      description={
        hasAssociatedApps ? (
          <>
            {dialogStrings?.['archive_component_apps_associated_description']}
            {totalAssociatedApps > 0 && (
              <p className="mt-2 text-sm text-white">
                <span className="font-bold">{totalAssociatedApps}</span>{' '}
                {dialogStrings?.apps_associated}
              </p>
            )}
            <div className="mt-3 flex items-center">
              <input
                type="checkbox"
                checked={isAppDisassociated}
                onChange={(e) => setIsAppDisassociated(e.target.checked)}
                className=" mr-2 h-[18px] w-[18px] rounded"
              />
              <label className="text-form-input-text text-[#EBEFF1]">
                {
                  dialogStrings?.[
                    'archive_component_apps_associated_subdescription'
                  ]
                }
              </label>
            </div>
          </>
        ) : (
          dialogStrings?.archive_component_no_apps_associated_description
        )
      }
      onConfirm={handleArchive}
      isLoading={archivingComponent}
      confirmButtonText={messages?.General?.archive}
      cancelButtonText={messages?.General?.cancel}
      confirmButtonDisabled={hasAssociatedApps && !isAppDisassociated}
    />
  );
};
